"use client";
import { IconButton } from "@offstreetparking/design-system/forms";
import { CloseIcon } from "@offstreetparking/design-system/icons/CloseIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { useEffect, useRef } from "react";

export const ContainedOverlay = ({
	isOpen,
	onClose,
	title,
	closeLabel,
	children,
	actions,
}: {
	isOpen: boolean;
	onClose?: () => void;
	title?: React.ReactNode;
	closeLabel: string;
	children: React.ReactNode;
	actions?: React.ReactNode;
}) => {
	const dialogRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const doc = dialogRef.current?.ownerDocument;
		if (!doc) {
			return;
		}
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose?.();
			}
		};
		doc.addEventListener("keydown", onKeyDown);
		return () => doc.removeEventListener("keydown", onKeyDown);
	}, [isOpen, onClose]);

	if (!isOpen) {
		return null;
	}

	return (
		<div
			ref={dialogRef}
			className="fixed inset-0 z-50 print:hidden"
			// biome-ignore lint/a11y/useSemanticElements: a native <dialog> would re-introduce top-layer behavior; containment requires in-place rendering
			role="dialog"
			aria-label={typeof title === "string" ? title : closeLabel}
		>
			<button
				type="button"
				aria-label={closeLabel}
				className="absolute inset-0 cursor-default bg-overlay"
				onClick={() => onClose?.()}
			/>
			<div className="absolute inset-0 flex items-end justify-center p-4 sm:items-center">
				<div className="relative flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white text-left shadow-xl">
					<div className="flex items-start justify-between gap-2 px-4 pt-4">
						<div className="min-w-0">
							{typeof title === "string" ? <Text weight="bold">{title}</Text> : title}
						</div>
						{onClose && (
							<IconButton
								icon={CloseIcon}
								aria-label={closeLabel}
								intent="tertiary"
								size="sm"
								onClick={onClose}
							/>
						)}
					</div>
					<div className="min-h-0 overflow-y-auto px-4 py-4">{children}</div>
					{actions && (
						<div className="flex justify-end gap-2 border-neutral-faded border-t px-4 py-3">{actions}</div>
					)}
				</div>
			</div>
		</div>
	);
};
